import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast
} from "@veritrellis/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useAdminPageMeta } from "../hooks/useAdminPageMeta.js";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";
import { PolicyBuilderForm } from "../components/policyBuilder/PolicyBuilderForm.js";
import { PolicyJsonEditor } from "../components/policyBuilder/PolicyJsonEditor.js";
import { PolicyPreviewPanel } from "../components/policyBuilder/PolicyPreviewPanel.js";
import { PolicySimulationPanel } from "../components/policyBuilder/PolicySimulationPanel.js";
import {
  defaultBuilderState,
  type BuilderState
} from "../components/policyBuilder/builderTypes.js";
import { parseToBuilder, parseJsonToBuilder } from "../components/policyBuilder/policyParser.js";
import { serializeToPolicy } from "../components/policyBuilder/policySerializer.js";
import { policyTemplates } from "../components/policyBuilder/templates.js";
import { PlanFeatureAlert } from "../components/billing/PlanFeatureAlert.js";
import { apiFetch } from "../lib/api.js";
import { AdminAnalyticsEvents, analytics } from "../lib/analytics.js";
import { showApiErrorToast } from "../lib/showApiErrorToast.js";
import { usePersistedAdminEnvironment } from "../hooks/usePersistedAdminEnvironment.js";
import { roleAtLeast, useCurrentRole } from "../hooks/useCurrentRole.js";
import { useWorkspaceBilling } from "../hooks/useWorkspaceBilling.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";
import { validateBuilderState } from "../components/policyBuilder/validation.js";
import { envLabel } from "../lib/requestPresentation.js";
import type { ActionPolicy } from "@veritrellis/shared";

type Environment = "sandbox" | "production";

type PolicyRow = {
  action_type: string;
  environment: Environment;
  mode: ActionPolicy["mode"];
  rules: unknown;
  default_decision: string | null;
  auto_allow_low_risk?: boolean;
  pack_id?: string | null;
  updated_at: string;
};

export const PolicyEditPage = () => {
  const { actionType: rawAction } = useParams();
  const [search] = useSearchParams();
  const envParam = search.get("env");
  const templateId = search.get("templateId");
  const [persistedEnv] = usePersistedAdminEnvironment();
  const environment: Environment =
    envParam === "production" ? "production" : envParam === "sandbox" ? "sandbox" : persistedEnv;
  const actionType = rawAction !== undefined ? decodeURIComponent(rawAction) : "";
  const { workspaceId, isLoading } = useWorkspaceId();
  const billingQ = useWorkspaceBilling(workspaceId);
  const { role } = useCurrentRole();
  const canEditPolicies = roleAtLeast(role, "developer");
  const navigate = useNavigate();

  const [builderState, setBuilderState] = useState<BuilderState>(defaultBuilderState());
  const [jsonText, setJsonText] = useState<string>("{}");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [builderCompatible, setBuilderCompatible] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("builder");

  const policies = useQuery({
    queryKey: ["policies", workspaceId],
    enabled: workspaceId !== null,
    queryFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/policies`);
      if (!res.ok) {
        throw new Error("policies_failed");
      }
      return (await res.json()) as { policies: PolicyRow[] };
    }
  });

  const initial =
    policies.data?.policies.find(
      (p) => p.action_type === actionType && p.environment === environment
    ) ?? null;

  const pageMeta = useMemo(() => {
    if (actionType.length === 0) {
      return null;
    }
    return {
      title: actionType,
      description:
        initial !== null
          ? `Last updated ${new Date(initial.updated_at).toLocaleString()} · ${envLabel(environment)}`
          : `New policy in ${envLabel(environment)}`,
      environmentScoped: false
    };
  }, [actionType, initial, environment]);

  useAdminPageMeta(pageMeta);

  useEffect(() => {
    if (initial === null) {
      const template =
        templateId != null ? policyTemplates.find((t) => t.id === templateId) : undefined;
      const freshState = template != null ? template.state : defaultBuilderState();
      setBuilderState(freshState);
      setJsonText(JSON.stringify(serializeToPolicy(freshState), null, 2));
      setBuilderCompatible(true);
      return;
    }
    const raw = {
      mode: initial.mode,
      rules: initial.rules ?? [],
      default_decision: initial.default_decision ?? "deny",
      auto_allow_low_risk: initial.auto_allow_low_risk === true ? true : undefined
    } as ActionPolicy;
    const { state, compatible } = parseToBuilder(raw);
    setBuilderState(state);
    setJsonText(JSON.stringify(raw, null, 2));
    setBuilderCompatible(compatible);
  }, [initial, templateId]);

  const liveJson = useMemo(
    () => JSON.stringify(serializeToPolicy(builderState), null, 2),
    [builderState]
  );

  const handleBuilderChange = (next: BuilderState) => {
    setBuilderState(next);
    setJsonText(JSON.stringify(serializeToPolicy(next), null, 2));
    setJsonError(null);
  };

  const handleJsonChange = (raw: string) => {
    setJsonText(raw);
    const parsed = parseJsonToBuilder(raw);
    if (parsed === null) {
      setJsonError("Invalid JSON — check brackets, quotes, and trailing commas.");
      return;
    }
    setJsonError(null);
    setBuilderCompatible(parsed.compatible);
    setBuilderState(parsed.state);
  };

  const save = async () => {
    if (workspaceId === null) {
      return;
    }
    let body: ActionPolicy;
    try {
      body = JSON.parse(jsonText) as ActionPolicy;
    } catch {
      toast.show({
        title: "Invalid JSON",
        message: "Fix the JSON before saving.",
        color: "red"
      });
      return;
    }
    if (billingQ.isSuccess) {
      const planErrors = validateBuilderState(builderState, billingQ.features).filter(
        (v) => v.severity === "error"
      );
      if (planErrors.length > 0) {
        toast.show({
          title: "Cannot save policy",
          message: planErrors[0]?.message ?? "This policy uses features not on your plan.",
          color: "red"
        });
        return;
      }
    }
    setSaving(true);
    const isNewPolicy = initial === null;
    const res = await apiFetch(
      `/admin/workspaces/${workspaceId}/policies/${environment}/${encodeURIComponent(actionType)}`,
      { method: "PUT", body: JSON.stringify(body) }
    );
    setSaving(false);
    if (res.ok) {
      analytics.trackEvent(
        isNewPolicy ? AdminAnalyticsEvents.policyCreated : AdminAnalyticsEvents.policyUpdated,
        { action_type: actionType, environment }
      );
      toast.show({ title: "Saved", message: "Policy updated.", color: "green" });
      void policies.refetch();
    } else {
      await showApiErrorToast(res);
    }
  };

  if (isLoading) {
    return <p style={{ margin: 0, fontSize: 14 }}>Loading…</p>;
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }
  if (actionType.length === 0) {
    return <p style={{ margin: 0, fontSize: 14 }}>Missing action type</p>;
  }
  if (policies.isLoading) {
    return <p style={{ margin: 0, fontSize: 14 }}>Loading…</p>;
  }
  if (policies.isError) {
    return (
      <p style={{ margin: 0, fontSize: 14, color: "var(--destructive)" }}>
        Failed to load policies.
      </p>
    );
  }

  const hasBlockingError = jsonError !== null;
  const planValidationErrors =
    billingQ.isSuccess && builderCompatible
      ? validateBuilderState(builderState, billingQ.features).filter((v) => v.severity === "error")
      : [];
  const hasPlanBlockingError = planValidationErrors.length > 0;
  const simulateDraftJson = activeTab === "json" ? jsonText : liveJson;

  return (
    <div className="admin-page-stack">
      <Link to="/policies" className="admin-breadcrumb">
        ← Policies
      </Link>

      <PageHeader
        actions={
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <Badge variant="outline">{environment}</Badge>
            {initial?.pack_id != null ? (
              <Badge variant="secondary">pack {initial.pack_id}</Badge>
            ) : null}
            <Button
              onClick={() => void save()}
              disabled={!canEditPolicies || hasBlockingError || hasPlanBlockingError || saving}
            >
              {saving ? <Spinner style={{ width: 16, height: 16, marginRight: 8 }} /> : null}
              Save
            </Button>
          </div>
        }
      />

      {environment === "production" ? (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTitle>Production</AlertTitle>
          <AlertDescription>
            Changes apply to new requests only. The previous policy is preserved in the audit log.
          </AlertDescription>
        </Alert>
      ) : null}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="builder" disabled={!builderCompatible && activeTab !== "builder"}>
            Rules
          </TabsTrigger>
          <TabsTrigger value="test">Test impact</TabsTrigger>
          <TabsTrigger value="json">JSON</TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="admin-policy-tab-content">
          {!builderCompatible ? (
            <Alert className="border-amber-200 bg-amber-50" style={{ marginBottom: 16 }}>
              <AlertDescription>
                This policy contains advanced constructs not fully editable in visual mode. JSON
                editing only.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="admin-policy-workspace">
            <section className="admin-panel">
              <div className="admin-panel-header">
                <h2 className="admin-panel-title">Rule ladder</h2>
              </div>
              <div className="admin-panel-body-padded">
                <div className="flex flex-col gap-4">
                  <PlanFeatureAlert
                    feature="feature_approval_groups"
                    enabled={billingQ.features.approvalGroups}
                    billingLoaded={billingQ.isSuccess}
                  />
                  <PolicyBuilderForm
                    state={builderState}
                    onChange={handleBuilderChange}
                    workspaceId={workspaceId}
                    environment={environment}
                    planFeatures={billingQ.features}
                    billingLoaded={billingQ.isSuccess}
                    disabled={!canEditPolicies || !builderCompatible}
                  />
                </div>
              </div>
            </section>
            <PolicyPreviewPanel
              state={builderState}
              actionType={actionType}
              environment={environment}
            />
          </div>
        </TabsContent>

        <TabsContent value="test" className="admin-policy-tab-content">
          <PolicySimulationPanel
            workspaceId={workspaceId}
            actionType={actionType}
            environment={environment}
            draftJson={simulateDraftJson}
            editPolicyHref={`/policies/${encodeURIComponent(actionType)}?env=${environment}`}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(
                  `/policies/${encodeURIComponent(actionType)}/simulate?env=${environment}`
                )
              }
            >
              Open full-screen simulator
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="json" className="admin-policy-tab-content">
          <PolicyJsonEditor
            jsonText={jsonText}
            onChange={handleJsonChange}
            jsonError={jsonError}
            disabled={!canEditPolicies}
            builderCompatible={builderCompatible}
            mode={builderState.mode}
            onModeChange={(m) => handleBuilderChange({ ...builderState, mode: m })}
            onResetFromBuilder={() => {
              const next = JSON.stringify(serializeToPolicy(builderState), null, 2);
              handleJsonChange(next);
            }}
          />
        </TabsContent>
      </Tabs>

      {activeTab !== "builder" ? (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button
            onClick={() => void save()}
            disabled={!canEditPolicies || hasBlockingError || hasPlanBlockingError || saving}
          >
            Save policy
          </Button>
        </div>
      ) : null}
    </div>
  );
};
